import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import WarningIcon from "@material-ui/icons/Warning";

import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { formatCurrency, formatDate } from "./format";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
}));

// labelKey é traduzido na renderização para acompanhar a troca de idioma
const SUB_STATUS = {
  active: { labelKey: "billingManager.subscriptionStatus.active", color: "#4caf50" },
  payment_failed: { labelKey: "billingManager.subscriptionStatus.paymentFailed", color: "#f44336" },
  cancelled: { labelKey: "billingManager.subscriptionStatus.cancelled", color: "#9e9e9e" },
  paused: { labelKey: "billingManager.subscriptionStatus.paused", color: "#ff9800" },
  pending: { labelKey: "billingManager.subscriptionStatus.pending", color: "#ff9800" },
};

const PAY_STATUS = {
  approved: { labelKey: "billingManager.companies.paymentStatus.approved", color: "#4caf50" },
  rejected: { labelKey: "billingManager.companies.paymentStatus.rejected", color: "#f44336" },
  pending: { labelKey: "billingManager.companies.paymentStatus.pending", color: "#ff9800" },
  cancelled: { labelKey: "billingManager.companies.paymentStatus.cancelled", color: "#9e9e9e" },
  refunded: { labelKey: "billingManager.companies.paymentStatus.refunded", color: "#9e9e9e" },
};

export default function CompaniesBilling() {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  // Por padrão exibe apenas empresas ativas; o switch inclui as desativadas
  const [showInactive, setShowInactive] = useState(false);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/overview");
      setCompanies(data.companies || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  // "só status === false significa inativa" — mesma semântica de Empresas
  const inactiveCount = companies.filter((c) => c.status === false).length;

  const filtered = companies
    .filter((c) => (showInactive ? true : c.status !== false))
    .filter((c) => {
      if (statusFilter === "all") return true;
      if (statusFilter === "blocked") return c.blocked;
      if (statusFilter === "overdue") return c.daysOverdue > 0;
      if (statusFilter === "subscription_active")
        return c.subscription?.status === "active";
      if (statusFilter === "subscription_failed")
        return c.subscription?.status === "payment_failed";
      return true;
    });

  return (
    <div className={classes.root}>
      <Box className={classes.filters}>
        <Typography variant="h6">{i18n.t("billingManager.companies.title")}</Typography>
        <TextField
          select
          size="small"
          variant="outlined"
          label={i18n.t("billingManager.companies.filter")}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ minWidth: 220 }}
        >
          <MenuItem value="all">{i18n.t("billingManager.all")}</MenuItem>
          <MenuItem value="overdue">{i18n.t("billingManager.companies.filters.overdue")}</MenuItem>
          <MenuItem value="blocked">{i18n.t("billingManager.companies.filters.blocked")}</MenuItem>
          <MenuItem value="subscription_active">
            {i18n.t("billingManager.companies.filters.subscriptionActive")}
          </MenuItem>
          <MenuItem value="subscription_failed">
            {i18n.t("billingManager.companies.filters.subscriptionFailed")}
          </MenuItem>
        </TextField>
        <Button size="small" variant="outlined" onClick={loadOverview}>
          {i18n.t("common.refresh")}
        </Button>
        {loading && <CircularProgress size={22} />}
        <Box flexGrow={1} />
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label={i18n.t("billingManager.companies.showInactive", { total: inactiveCount })}
        />
      </Box>

      <Paper variant="outlined" className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("common.company")}</TableCell>
              <TableCell>{i18n.t("billingManager.columns.plan")}</TableCell>
              <TableCell align="center">{i18n.t("billingManager.columns.dueDate")}</TableCell>
              <TableCell align="center">{i18n.t("billingManager.columns.status")}</TableCell>
              <TableCell align="center">{i18n.t("billingManager.columns.subscription")}</TableCell>
              <TableCell align="center">{i18n.t("billingManager.columns.lastCharge")}</TableCell>
              <TableCell align="center">{i18n.t("billingManager.columns.lastPayment")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((company) => {
              const sub = company.subscription;
              const subInfo = sub ? SUB_STATUS[sub.status] : null;
              const pay = company.lastPayment;
              const payInfo = pay ? PAY_STATUS[pay.status] : null;
              return (
                <TableRow key={company.id}>
                  <TableCell>
                    {company.name}
                    {!company.status && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        {i18n.t("billingManager.companyDisabled")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {company.plan?.name || "-"}
                    {company.plan && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        {i18n.t("billingManager.companies.perMonth", {
                          value: formatCurrency(company.plan.value),
                        })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={
                      company.daysOverdue > 0
                        ? { color: "#f44336", fontWeight: "bold" }
                        : undefined
                    }
                  >
                    {formatDate(company.dueDate)}
                    {company.daysOverdue > 0 && (
                      <Typography variant="caption" display="block">
                        {i18n.t("billingManager.daysOverdue", {
                          count: company.daysOverdue,
                        })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {company.blocked ? (
                      <Chip
                        size="small"
                        label={i18n.t("billingManager.companies.situation.blocked")}
                        style={{ backgroundColor: "#f44336", color: "#fff" }}
                      />
                    ) : company.daysOverdue > 0 ? (
                      <Chip
                        size="small"
                        label={i18n.t("billingManager.companies.situation.gracePeriod")}
                        style={{ backgroundColor: "#ff9800", color: "#fff" }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label={i18n.t("billingManager.companies.situation.upToDate")}
                        style={{ backgroundColor: "#4caf50", color: "#fff" }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {subInfo ? (
                      <Box display="flex" alignItems="center" justifyContent="center" gridGap={4}>
                        <Chip
                          size="small"
                          label={i18n.t(subInfo.labelKey)}
                          style={{ backgroundColor: subInfo.color, color: "#fff" }}
                        />
                        {sub.amountDiverges && (
                          <Tooltip
                            title={i18n.t("billingManager.companies.amountDiverges", {
                              subscriptionAmount: formatCurrency(sub.amount),
                              planAmount: formatCurrency(company.plan?.value),
                            })}
                          >
                            <WarningIcon style={{ color: "#ff9800" }} fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {sub?.lastChargeDate ? (
                      <>
                        {moment(sub.lastChargeDate).format("DD/MM/YYYY")}
                        <Typography
                          variant="caption"
                          display="block"
                          style={{
                            color:
                              sub.lastChargeStatus === "approved"
                                ? "#4caf50"
                                : "#f44336",
                          }}
                        >
                          {sub.lastChargeStatus === "approved"
                            ? i18n.t("billingManager.companies.lastCharge.approved")
                            : i18n.t("billingManager.companies.lastCharge.rejected")}
                        </Typography>
                      </>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {pay ? (
                      <>
                        {formatCurrency(pay.value)}
                        <Typography
                          variant="caption"
                          display="block"
                          style={{ color: payInfo?.color }}
                        >
                          {payInfo ? i18n.t(payInfo.labelKey) : pay.status} —{" "}
                          {moment(pay.paidAt || pay.createdAt).format("DD/MM/YY")}
                        </Typography>
                      </>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("billingManager.companies.empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
}
